import { Skeleton } from "@credit-acceptance/uikit";
import { PhoneIcon, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "../../../components/ui/dialog";
import { getErrorMessage } from "../../../lib/query/errors";
import { useDealerInfo } from "../api/useDealerInfo";
import { mapDealerInfoResponseToViewModel } from "./dealerInfoModal.mapping";
import type { DealerInfoViewModel } from "./DealerInfoModal.types";

export const DEALER_INFO_MODAL_ID = "dealer-info-modal";

interface DealerInfoModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  approvalId: string;
}

const EMPTY = "—";

function display(value: string | undefined): string {
  return value?.trim() ? value : EMPTY;
}

/** A field row: label on the left, value (optionally a click-to-call phone or external link) on the right. */
function InfoRow({ label, value, phone, link }: { label: string; value: string; phone?: boolean; link?: boolean }) {
  const shown = display(value);
  return (
    <div role="row" className="contents">
      <span role="cell" className="text-muted-foreground min-w-36 py-1 pr-6 text-xs font-normal">
        {label}:
      </span>
      <span role="cell" className="text-foreground py-1 text-xs font-medium">
        {phone && shown !== EMPTY ? (
          <span className="inline-flex items-center gap-1.5">
            {shown}
            <a
              href={`tel:${shown.replaceAll(/[^\d+]/g, "")}`}
              className="text-success-foreground border-success-foreground hover:bg-success flex size-6 shrink-0 items-center justify-center rounded-md border transition-colors"
            >
              <PhoneIcon className="size-3.5 fill-current" aria-hidden="true" />
            </a>
          </span>
        ) : link && shown !== EMPTY ? (
          <a href={shown} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
            {shown}
          </a>
        ) : (
          shown
        )}
      </span>
    </div>
  );
}

/** Section divider bar (e.g. "Sales Person", "Volume", "Other"). */
function SectionHeader({ label }: { label: string }) {
  return (
    <div className="bg-muted text-muted-foreground col-span-2 mb-1 mt-3 rounded px-2 py-1 text-xs font-semibold uppercase tracking-wide">
      {label}
    </div>
  );
}

// Volume section disabled: no data source in getDealerInfo (SVRS-41708).

const EMPTY_DEALER_INFO: DealerInfoViewModel = {
  nameOnContract: "",
  dba: "",
  physicalLocation: "",
  dealershipNumber: "",
  dealerFax: "",
  dealerRating: "",
  originalActivationDate: "",
  websiteUrl: "",
  salesPersonName: "",
  salesPersonNumber: "",
  contractsInAh: "",
  volumeMonths: [],
  volumeTotal: "",
  avgDaysToFundTotal: "",
  mamName: "",
  mamPhone: "",
  dosName: "",
  dosPhone: "",
};

/**
 * Dealer Information modal, opened from the dealer name/id link in
 * `DealIntelligenceCard`. Fetches `DealerInfoApi.getDealerInfo(approvalId)`
 * while open (see `useDealerInfo`) and maps the response via
 * `mapDealerInfoResponseToViewModel`. The Volume / Avg Days to Fund /
 * contracts-in-AH section has no data source in this endpoint (SVRS-41708)
 * and always renders "—".
 */
function DealerInfoModal({ open, onOpenChange, approvalId }: DealerInfoModalProperties) {
  const { t } = useTranslation();
  const { data: response, isLoading, isError, error } = useDealerInfo(approvalId, open);
  const info = response ? mapDealerInfoResponseToViewModel(response) : EMPTY_DEALER_INFO;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        id={DEALER_INFO_MODAL_ID}
        showCloseButton={false}
        className="gap-0 overflow-hidden p-0 sm:max-w-xl"
      >
        <div className="bg-primary text-primary-foreground flex items-center justify-between px-5 py-2.5">
          <DialogTitle className="text-primary-foreground text-sm font-semibold">{t("dealerInfo.title")}</DialogTitle>
          <DialogDescription className="sr-only">{t("dealerInfo.title")}</DialogDescription>
          <DialogClose
            className="text-primary-foreground/80 hover:text-primary-foreground inline-flex items-center transition-colors"
            aria-label={t("dealerInfo.close")}
          >
            <X className="size-3.5" aria-hidden="true" />
          </DialogClose>
        </div>

        <div className="flex flex-col gap-1 bg-white px-5 py-3">
          {isLoading && (
            <div role="status" aria-label={t("dealerInfo.loading")} className="py-2">
              <Skeleton loading active paragraph={{ rows: 8 }} />
            </div>
          )}

          {isError && !isLoading && (
            <p role="alert" className="flex-1 py-8 text-center text-sm">
              {t("dealerInfo.loadError", { message: getErrorMessage(error) })}
            </p>
          )}

          {!isLoading && !isError && (
            <>
              <div role="table" className="grid grid-cols-[max-content_1fr]">
                <InfoRow label={t("dealerInfo.nameOnContract")} value={info.nameOnContract} />
                <InfoRow label={t("dealerInfo.dba")} value={info.dba} />
                <InfoRow label={t("dealerInfo.physicalLocation")} value={info.physicalLocation} />
                <InfoRow label={t("dealerInfo.dealershipNumber")} value={info.dealershipNumber} phone />
                <InfoRow label={t("dealerInfo.dealerFax")} value={info.dealerFax} />
                <InfoRow label={t("dealerInfo.dealerRating")} value={info.dealerRating} />
                <InfoRow label={t("dealerInfo.originalActivationDate")} value={info.originalActivationDate} />
                <InfoRow label={t("dealerInfo.websiteAddress")} value={info.websiteUrl} link />

                <SectionHeader label={t("dealerInfo.salesPersonSection")} />
                <InfoRow label={t("dealerInfo.salesPerson")} value={info.salesPersonName} />
                <InfoRow label={t("dealerInfo.salesPersonNumber")} value={info.salesPersonNumber} phone />

                {/* Volume section disabled: no data source in getDealerInfo (SVRS-41708), always rendered "—".
                <SectionHeader label={t("dealerInfo.volumeSection")} />
                <InfoRow label={t("dealerInfo.contractsInAh")} value={info.contractsInAh} />
                */}
              </div>

              {/* <VolumeTable data={info} /> */}

              <div role="table" className="grid grid-cols-[max-content_1fr]">
                <SectionHeader label={t("dealerInfo.otherSection")} />
                <InfoRow label={t("dealerInfo.mamName")} value={info.mamName} />
                <InfoRow label={t("dealerInfo.mamPhone")} value={info.mamPhone} phone />
                <InfoRow label={t("dealerInfo.dosName")} value={info.dosName} />
                <InfoRow label={t("dealerInfo.dosPhone")} value={info.dosPhone} phone />
              </div>
            </>
          )}
        </div>

        <div className="flex justify-center px-5 py-2">
          <DialogClose asChild>
            <Button type="button" size="sm">
              {t("dealerInfo.close")}
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DealerInfoModal;
